/**
 * RevokeConfirmModal — Destructive revoke confirmation (WORKER-7.4).
 *
 * On confirm: calls POST /api/admin/workers/{id}/revoke (server cascade-deletes
 * worker_session rows). The worker's next online API call returns 401
 * ERR_WORKER_REVOKED_TERMINAL → mobile RevokedTerminalScreen (Plan 05).
 */
import { type SyntheticEvent, useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { useRevokeWorker } from "../../hooks/useRevokeWorker";
import { Button } from "../../components/Button";
import { Banner } from "../../components/Banner";

interface RevokeConfirmModalProps {
  workerId: number;
  workerName: string;
  onClose: () => void;
}

export function RevokeConfirmModal({ workerId, workerName, onClose }: RevokeConfirmModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const revokeWorker = useRevokeWorker();

  useEffect(() => {
    dialogRef.current?.showModal();
    return () => {
      dialogRef.current?.close();
    };
  }, []);

  const handleDialogCancel = (e: SyntheticEvent) => {
    e.preventDefault();
  };

  const handleConfirm = () => {
    revokeWorker.mutate(
      { worker_id: workerId },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  return (
    <dialog
      ref={dialogRef}
      onCancel={handleDialogCancel}
      data-testid="revoke-confirm-modal"
      className="rounded-lg border border-divider bg-surface-raised shadow-lg p-0 backdrop:bg-black/40 inline-size-[400px] max-inline-size-[calc(100vw-2rem)]"
    >
      <div className="ps-6 pe-6 pbs-6 pbe-6">
        <h2 className="text-xl font-semibold text-on-surface mbe-4">
          <FormattedMessage id="LABEL_REVOKE_WORKER_CONFIRM" values={{ name: workerName }} />
        </h2>

        <p className="text-base text-on-surface-variant mbe-6">
          <FormattedMessage id="LABEL_REVOKE_BODY" values={{ name: workerName }} />
        </p>

        {revokeWorker.isError && (
          <div className="mbe-4">
            <Banner variant="error" data-testid="revoke-error-banner">
              <FormattedMessage id="ERR_GENERIC" />
            </Banner>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={revokeWorker.isPending}
            data-testid="revoke-cancel-button"
          >
            <FormattedMessage id="LABEL_CANCEL" />
          </Button>
          <Button
            variant="destructive"
            type="button"
            onClick={handleConfirm}
            disabled={revokeWorker.isPending}
            data-testid="revoke-confirm-button"
          >
            <FormattedMessage id="LABEL_REVOKE_CONFIRM" />
          </Button>
        </div>
      </div>
    </dialog>
  );
}
